import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Users, Car, Calendar, MapPin, LogOut, Settings } from "lucide-react";

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total_users: 0,
    total_vehicles: 0,
    total_slots: 0,
    booked_slots: 0,
    available_slots: 0,
    pending_carpool_requests: 0,
  });
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [carpools, setCarpools] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchBookings();
    fetchCarpools();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings`);
      setBookings(res.data.slice(0, 10));
    } catch (e) {
      toast.error("Failed to load bookings");
    }
  };

  const fetchCarpools = async () => {
    try {
      const res = await axios.get(`${API}/admin/carpool-requests`);
      setCarpools(res.data || []);
    } catch (e) {
      toast.error("Failed to load carpool requests");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  const utilizationPercentage =
    stats.total_slots > 0 ? Math.round((stats.today_bookings / stats.total_slots) * 100) : 0;

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      {/* Floating Navigation */}
      <nav className="floating-nav">
        <div className="flex items-center justify-between gap-6">
          <button onClick={() => navigate("/admin")} className="text-[#1A3C34] font-bold text-xl" style={{width:'120px'}}>
             <img src="../parkioo_logo.png" alt="Parkioo Logo" className="h-50 w-full object-contain p-2" />
          </button>
          <div className="flex gap-4">
            <Button
              data-testid="admin-nav-dashboard"
              onClick={() => navigate("/admin")}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              Dashboard
            </Button>
            <Button
              data-testid="admin-nav-users"
              onClick={() => navigate("/admin/users")}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              Users
            </Button>
            <Button
              data-testid="admin-nav-slots"
              onClick={() => navigate("/admin/slots")}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              Parking Slots
            </Button>
            <Button
              data-testid="admin-nav-timeslots"
              onClick={() => navigate("/admin/timeslots")}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              Time Slots
            </Button>
            <Button
              data-testid="nav-bookings-today"
              onClick={() => navigate("/admin/bookings-today")}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              Today's Bookings
            </Button>
            <Button
              data-testid="admin-nav-logout"
              onClick={handleLogout}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              <LogOut size={20} />
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-32 pb-12 px-6 max-w-7xl mx-auto">
        <div className="mb-12 fade-in">
          <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">
            Admin Dashboard
          </h1>
          <p className="text-xl text-gray-600">Monitor and manage your parking system.</p>
        </div>

        {/* Stats Grid */}
        <div className="bento-grid mb-12">
          <Card className="p-8 bg-gradient-to-br from-[#1A3C34] to-[#2D5F52] text-white border-none col-span-1 md:col-span-2 row-span-2">
            <div className="h-full flex flex-col justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-4">Parking Utilization</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm opacity-90">Booked</span>
                      <span className="text-sm opacity-90">
                        {stats.today_bookings} / {stats.total_slots}
                      </span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-3">
                      <div
                        className="bg-[#FF6B35] h-3 rounded-full transition-all duration-500"
                        style={{ width: `${utilizationPercentage}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-6xl font-bold mt-8">{utilizationPercentage}%</div>
                  <p className="text-sm opacity-90">Current utilization rate</p>
                </div>
              </div>
              <Button
                data-testid="manage-slots-btn"
                onClick={() => navigate("/admin/bookings-today")}
                className="mt-6 bg-white text-[#1A3C34] hover:bg-gray-100 rounded-full"
              >
                Manage Slots
              </Button>
            </div>
          </Card>

          <Card className="p-8 bg-[#F5F2EB] border-none">
            <Users className="mb-4 text-[#1A3C34]" size={32} />
            <p className="text-sm text-gray-600 mb-2">Total Users</p>
            <p className="text-4xl font-bold text-[#1A3C34]">{stats.total_users}</p>
          </Card>

          <Card className="p-8 bg-white border border-gray-100">
            <Car className="mb-4 text-[#1A3C34]" size={32} />
            <p className="text-sm text-gray-600 mb-2">Total Vehicles</p>
            <p className="text-4xl font-bold text-[#1A3C34]">{stats.total_vehicles}</p>
          </Card>

          <Card className="p-8 bg-[#FF6B35] text-white border-none">
            <MapPin className="mb-4" size={32} />
            <p className="text-sm opacity-90 mb-2">Available Slots</p>
            <p className="text-4xl font-bold">{stats.available_slots}</p>
          </Card>

          <Card className="p-8 bg-white border border-gray-100">
            <Calendar className="mb-4 text-[#1A3C34]" size={32} />
            <p className="text-sm text-gray-600 mb-2">Pending Carpool</p>
            <p className="text-4xl font-bold text-[#1A3C34]">{stats.pending_carpool_requests}</p>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="fade-in">
          <h2 className="text-3xl font-bold text-[#1A3C34] mb-6">Quick Actions</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-8 border border-gray-100 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate("/admin/users")}>
              <Users className="mb-4 text-[#1A3C34]" size={32} />
              <h3 className="text-xl font-bold text-[#1A3C34] mb-2">View All Users</h3>
              <p className="text-gray-600">Monitor user registrations and activities</p>
            </Card>

            <Card className="p-8 border border-gray-100 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate("/admin/slots")}>
              <MapPin className="mb-4 text-[#1A3C34]" size={32} />
              <h3 className="text-xl font-bold text-[#1A3C34] mb-2">Manage Parking</h3>
              <p className="text-gray-600">Configure parking slots and capacity</p>
            </Card>

            <Card className="p-8 border border-gray-100 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate("/admin/timeslots")}>
              <Calendar className="mb-4 text-[#1A3C34]" size={32} />
              <h3 className="text-xl font-bold text-[#1A3C34] mb-2">Time Slots</h3>
              <p className="text-gray-600">Configure booking time slots</p>
            </Card>
          </div>
        </div>
      </div>

      {/* Organization Bookings & Carpool */}
      <div className="pt-8 px-6 max-w-7xl mx-auto">
        <h2 className="text-2xl font-bold text-[#1A3C34] mb-4">Recent Bookings (Your Organization)</h2>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {bookings.length === 0 ? (
            <Card className="p-6">No recent bookings</Card>
          ) : (
            bookings.map((b) => (
              <Card key={b._id} className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-600">{b.user_name || 'Unknown'}</div>
                    <div className="text-lg font-bold">{b.slot_number || 'N/A'}</div>
                    <div className="text-sm text-gray-500">{b.date} • {b.time_slot?.name || ''}</div>
                  </div>
                  <div className="text-sm font-semibold text-[#1A3C34]">{b.status}</div>
                </div>
              </Card>
            ))
          )}
        </div>

        <h2 className="text-2xl font-bold text-[#1A3C34] mb-4">Carpool Requests (Your Organization)</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {carpools.length === 0 ? (
            <Card className="p-6">No carpool requests</Card>
          ) : (
            (carpools || []).slice(0, 8).map((r) => (
              <Card key={r._id} className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-600">From: {r.requester_name || r.requester?.name}</div>
                    <div className="text-lg font-bold">For: {r.date}</div>
                    <div className="text-sm text-gray-500">{r.message}</div>
                  </div>
                  <div className="text-sm font-semibold text-[#1A3C34]">{r.status}</div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;